/**
 * Network and auth side effects for the Connect Splyt page. The pure parts
 * (URLs, parsing, copy) live in connectHelpers.js.
 */
import firebase from '../../services/firebase';
import { subscribeToLoginChanges, getProfileName } from '../../API/users';
import { withTimeout } from '../join/inviteHelpers';
import {
  apiBase,
  buildApiUrls,
  parseConnectRequest,
  parseRedirectUrl,
  connectErrorMessage,
  isGoneStatus,
} from './connectHelpers';

const REQUEST_TIMEOUT_MS = 12000;
const AUTH_RESTORE_TIMEOUT_MS = 4000;
const NAME_READ_TIMEOUT_MS = 4000;

const urls = buildApiUrls(apiBase());

export class ConnectApiError extends Error {
  constructor(message, { status = null, gone = false } = {}) {
    super(message);
    this.name = 'ConnectApiError';
    this.status = status;
    this.gone = gone;
  }
}

async function readJson(response) {
  try {
    return await response.json();
  } catch (err) {
    return null;
  }
}

async function fetchJson(url, options, clientName) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;
  let response;
  try {
    response = await fetch(url, { ...options, signal: controller ? controller.signal : undefined });
  } catch (err) {
    throw new ConnectApiError(connectErrorMessage(null, null, clientName));
  } finally {
    if (timer) clearTimeout(timer);
  }
  const body = await readJson(response);
  if (!response.ok) {
    throw new ConnectApiError(connectErrorMessage(response.status, body, clientName), {
      status: response.status,
      gone: isGoneStatus(response.status),
    });
  }
  return body;
}

/** GET the pending request. Throws ConnectApiError; `gone` marks 404 and 410. */
export async function fetchConnectRequest(requestId) {
  const body = await fetchJson(urls.request(requestId), { method: 'GET' });
  const parsed = parseConnectRequest(body);
  if (!parsed) throw new ConnectApiError(connectErrorMessage(404, body), { status: 404, gone: true });
  return parsed;
}

/** Approve as the signed in user. Resolves with the redirect URL. */
export async function approveConnect(requestId, name, clientName) {
  const user = firebase.auth().currentUser;
  if (!user || user.isAnonymous) {
    throw new ConnectApiError(connectErrorMessage(401, null, clientName), { status: 401 });
  }
  const idToken = await user.getIdToken();
  const payload = { requestId };
  if (name) payload.name = name;
  const body = await fetchJson(urls.approve, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(payload),
  }, clientName);
  const redirectUrl = parseRedirectUrl(body);
  if (!redirectUrl) throw new ConnectApiError(connectErrorMessage(null, null, clientName));
  return redirectUrl;
}

/** Deny. Resolves with the redirect URL that carries the error back. */
export async function denyConnect(requestId, clientName) {
  const body = await fetchJson(urls.deny, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ requestId }),
  }, clientName);
  const redirectUrl = parseRedirectUrl(body);
  if (!redirectUrl) throw new ConnectApiError(connectErrorMessage(null, null, clientName));
  return redirectUrl;
}

/**
 * The restored Firebase user, or whatever currentUser holds once the restore
 * has waited long enough. Resolves once; never rejects.
 */
export function waitForAuthUser() {
  return new Promise((resolve) => {
    let settled = false;
    let timer = null;
    let unsubscribe = null;
    const finish = (user) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      if (unsubscribe) unsubscribe();
      resolve(user || null);
    };
    timer = setTimeout(() => finish(firebase.auth().currentUser), AUTH_RESTORE_TIMEOUT_MS);
    unsubscribe = subscribeToLoginChanges(finish);
    if (settled) unsubscribe();
  });
}

/**
 * True when the profile needs a name. A stalled or failed read counts as
 * nameless: typing a name is the safe side.
 */
export async function profileNeedsName(uid) {
  if (!uid) return true;
  try {
    const name = await withTimeout(getProfileName(uid), NAME_READ_TIMEOUT_MS, '');
    return !name;
  } catch (err) {
    console.error('[connect] name check failed:', err);
    return true;
  }
}
